/**
 * The season model's PURE decisions — extracted so they can be gated headlessly
 * against real data, instead of only being exercised by a human driving the app.
 *
 * Three facts live here, each with exactly one owner:
 *
 *   1. `groupRunningDates` — WHICH DATES SHARE A TIMETABLE. One group per
 *      timetable COLOUR (Chris, 2026-08-25: "there should only be 1 timetable
 *      per colour"), with each group's dates broken down by sales day-type in
 *      `spans` so the money can still be worked out day by day.
 *   2. `blendParts` — HOW SEVERAL DAY-TYPES BECOME ONE DAY to design against.
 *      Conserves passengers and revenue exactly.
 *   3. `chooseSalesSeason` — WHICH SEASON'S SALES a month is modelled on.
 *
 * ResearchStudio.tsx delegates to these; it does not re-implement them. That is
 * the point — two code paths answering one question is the defect shape this
 * app has been repeatedly bitten by.
 */
import type { ClassDemand, Departure, Slot } from './types'

// ---------------------------------------------------------------------------
//  1. Grouping — one timetable per colour
// ---------------------------------------------------------------------------

/** The dates of one colour that share a sales day-type. */
export interface DayClassSpan { dayClass: string; dates: string[] }

/**
 * One timetable COLOUR's running days in a month — the unit of design.
 * `dayClass` is 'Week' on every group produced here; it exists only so plans
 * banked before 2026-08-25 (one per day-type) still resolve by their own class.
 */
export interface SeasonDayGroup { colour: string; dayClass: string; dates: string[]; spans?: DayClassSpan[] }

/** A date's colour and whether it runs, from thebigplan's calendar. */
export interface CalendarDay { date: string; colour: string; run: boolean }
/** A date in the planner's applied running set. */
export interface AppliedDay { date: string; day_class: string; running: boolean; colour?: string }

/** A colour with no standard pattern to score (galas, diners, blank). */
export function unscorableColour(colour: string): boolean {
  const c = (colour ?? '').toUpperCase()
  return !c || c === 'NONE' || c === 'DINER' || c === 'GALA'
}

/**
 * Decompose one month into its timetable colours.
 *
 * TWO SOURCES, ONE JOB EACH:
 *   - WHICH DATES RUN comes from the APPLIED RUNNING SET whenever one covers
 *     this month — the planner's curated truth. A month the set does not cover
 *     falls back to the calendar rather than silently reporting nothing.
 *   - WHICH COLOUR a date runs comes from thebigplan, unless the planner has
 *     assigned one (that override wins, and makes an otherwise colourless day
 *     scorable).
 *
 * `?? false` on a missing calendar row is deliberate: no evidence is not
 * permission to assume the railway ran (law 8).
 */
export function groupRunningDates(args: {
  monthKey: string
  calendarDays: CalendarDay[]
  appliedDays: AppliedDay[] | null
  classesInGrain: readonly string[]
}): { runDays: number; groups: SeasonDayGroup[]; source: 'set' | 'calendar' } {
  const { monthKey: mk, calendarDays, appliedDays, classesInGrain } = args
  const clsByDate = new Map((appliedDays ?? []).map((d) => [d.date, d.day_class]))
  const colourByDate = new Map(
    calendarDays.filter((d) => d.date.startsWith(mk)).map((d) => [d.date, (d.colour ?? '').trim()])
  )
  for (const d of appliedDays ?? []) {
    if (d.colour && d.date.startsWith(mk)) colourByDate.set(d.date, d.colour.trim())
  }
  const setDates = (appliedDays ?? []).filter((d) => d.date.startsWith(mk))
  const useSet = setDates.length > 0
  const runningDates = (useSet
    ? setDates.filter((d) => d.running).map((d) => d.date)
    : calendarDays.filter((d) => d.date.startsWith(mk) && d.run).map((d) => d.date)
  ).slice().sort()

  const by = new Map<string, SeasonDayGroup>()
  let runDays = 0
  for (const date of runningDates) {
    runDays += 1
    const colour = colourByDate.get(date) ?? ''
    const dow = new Date(`${date}T12:00:00Z`).getUTCDay()
    const coarse = dow === 0 ? 'Sun' : dow === 6 ? 'Sat' : 'Wkdy'
    const mapped = clsByDate.get(date)
    const dayClass = mapped && classesInGrain.includes(mapped) ? mapped : coarse
    const key = colour.toUpperCase()
    let g = by.get(key)
    if (!g) { g = { colour, dayClass: 'Week', dates: [], spans: [] }; by.set(key, g) }
    g.dates.push(date)
    const sp = g.spans!.find((x) => x.dayClass === dayClass)
    if (sp) sp.dates.push(date)
    else g.spans!.push({ dayClass, dates: [date] })
  }
  for (const g of by.values()) g.spans!.sort((a, b) => b.dates.length - a.dates.length)
  return { runDays, groups: [...by.values()], source: useSet ? 'set' : 'calendar' }
}

// ---------------------------------------------------------------------------
//  2. The blend — several day-types, one day to design against
// ---------------------------------------------------------------------------

export interface DemandPart { dayClass: string; days: number; cls: ClassDemand; un: number }

/**
 * Blend day-types into ONE representative day, weighted by date count.
 *
 * Slots match on (minute, origin, destination). Passengers are date-weighted;
 * the fare per passenger is PASSENGER-weighted. So over the group's dates the
 * blended day reproduces the total passengers and the total revenue of its
 * parts EXACTLY — the blend decides which train to build, never what it earns.
 */
export function blendParts(parts: DemandPart[]): { cls: ClassDemand; un: number } {
  const D = parts.reduce((t, p) => t + p.days, 0) || 1
  const dir = (pick: (c: ClassDemand) => Slot[]): Slot[] => {
    const by = new Map<string, { s: Slot; pax: number; net: number; ow: number }>()
    for (const p of parts) {
      for (const sl of pick(p.cls)) {
        const k = `${sl.minutes}|${sl.o}|${sl.d}`
        const cur = by.get(k) ?? { s: sl, pax: 0, net: 0, ow: 0 }
        cur.pax += sl.paxPerDay * p.days
        cur.net += sl.paxPerDay * p.days * sl.netPerPax
        // The one-way-today share blends passenger-weighted, like the fare.
        cur.ow += sl.paxPerDay * p.days * (sl.oneWayShare ?? 0)
        by.set(k, cur)
      }
    }
    return [...by.values()]
      .map(({ s: sl, pax, net, ow }) => ({
        ...sl, paxPerDay: pax / D, netPerPax: pax > 0 ? net / pax : sl.netPerPax,
        ...(pax > 0 && ow > 0 ? { oneWayShare: Math.min(1, ow / pax) } : {}),
      }))
      .sort((a, b) => a.minutes - b.minutes)
  }
  return {
    cls: { cls: 'Week', weight: 1, north: dir((c) => c.north), south: dir((c) => c.south) },
    un: parts.reduce((t, p) => t + p.un * p.days, 0) / D,
  }
}

// ---------------------------------------------------------------------------
//  3. Which season's sales a month is modelled on (law 6)
// ---------------------------------------------------------------------------

/**
 * ★ LAW 6, GENERALISED — WHICH SEASON'S SALES A MONTH IS MODELLED ON.
 *
 * The Studio's job is to design the season the railway is ABOUT to run
 * (Chris, 2026-08-25: "the entire point of this studio is that you're actually
 * designing the 2027 running season... what you project is what we are going
 * to run — get this wrong and we'll bankrupt the railway"). A season being
 * designed has no sales of its own, so its demand must come from history — and
 * choosing the wrong piece of history is the highest-stakes error this app can
 * make, because the answer will look entirely self-consistent.
 *
 * The rules, in order:
 *
 *  1. A candidate season is UNUSABLE for a month if it sold nothing that month
 *     (no evidence) or if the planner has DECLARED that month disrupted.
 *  2. A month that has already RUN and is usable uses its OWN sales. Actual
 *     trading beats any forecast.
 *  3. Otherwise walk BACK through the seasons and take the newest usable one.
 *     This is what makes designing 2027/28 safe: March 2027 has no sales, and
 *     March 2026 is declared disrupted (Bridge 42 closed the line north of
 *     Goathland), so it must reach past it to March 2025 — a real Whitby
 *     railway. Falling back only ONE season would have handed the planner a
 *     2027 plan for a severed railway.
 *  4. A month with no usable season anywhere gets NOTHING, and says so. An
 *     invented basis is worse than a stated gap (law 8).
 *
 * Every rejection is recorded in `considered`, so the screen and the board
 * paper can show not just which year the figures came from but which years were
 * passed over and why.
 */

/** This month, as one candidate season sold it. */
export interface SeasonCandidate {
  /** Season label, e.g. '2026/2027'. */
  season: string
  /** The calendar year this month falls in for that season. */
  yr: number
  /** Passengers that season sold in this month. Zero means no evidence. */
  pax: number
}

export interface SalesBasis {
  /** The season to model this month on. Null = no usable evidence anywhere. */
  chosen: SeasonCandidate | null
  /** True when `chosen` is the month's own season (it has already traded). */
  own: boolean
  /** Every candidate weighed, newest first, with why it was passed over. */
  considered: { season: string; yr: number; pax: number; rejected: string | null }[]
  /** Plain English, for the screen and the board paper. Never silent. */
  basis: string
}

export function chooseSalesBasis(args: {
  /** The month being designed, 'YYYY-MM'. */
  monthKey: string
  /** Today, 'YYYY-MM' — a month at or after this has not finished trading. */
  nowKey: string
  /** This month in each season, NEWEST FIRST, starting with the month's own. */
  candidates: SeasonCandidate[]
  /**
   * ★ THE PLANNER'S EXPLICIT CHOICE, month key -> season label.
   *
   * Chris, 2026-08-25: *"for each month give me a drop down which lists the
   * running season I can take that months data from — that is the data, the
   * sales, the capacity, the turns, the timetable you will use to generate
   * data."* This beats every automatic rule below. It also replaces the old
   * "excluded months" idea: Bridge 42 is no longer a special concept, it is
   * three ordinary choices (March/April/May 2026 -> 2025/2026).
   *
   * It cascades, which is the point. A month REDIRECTED away from its own
   * season is, by the planner's own declaration, unrepresentative — so it is
   * refused as a SOURCE for any other month too. That is what stops a 2027
   * design quietly reaching into the Bridge 42 spring.
   */
  monthSources?: Record<string, string>
  /** Absolute 'YYYY-MM' keys the planner has declared disrupted (legacy). */
  disruptedMonths: string[]
  /** Month names for the prose, e.g. ['Jan','Feb',...]. */
  monthNames?: string[]
}): SalesBasis {
  const { monthKey, nowKey, candidates, disruptedMonths, monthNames } = args
  const monthSources = args.monthSources ?? {}
  const mm = Number(monthKey.slice(5, 7))
  const mName = monthNames?.[mm - 1] ?? monthKey.slice(5, 7)
  const considered: SalesBasis['considered'] = []

  // The disrupted test is against the CANDIDATE's own absolute month, not the
  // month being designed. Getting this backwards is how a 2027 design would
  // have quietly inherited 2026's closure: the exception is declared as
  // '2026-03', and it is 2026-03 that must be refused as a SOURCE.
  const keyOf = (c: SeasonCandidate) => `${c.yr}-${String(mm).padStart(2, '0')}`
  const reject = (c: SeasonCandidate): string | null => {
    // A month the planner has pointed AWAY from its own season is not
    // representative of itself, so it cannot be a source for anything else.
    const own = monthSources[keyOf(c)]
    if (own && own !== c.season) return `the planner takes ${keyOf(c)} from ${own}, so it does not describe itself`
    if (disruptedMonths.includes(keyOf(c))) return 'declared disrupted by the planner'
    // A month that has not FINISHED trading is not evidence. This one nearly
    // got through: designing September 2027 from September 2026 looked correct
    // — 2026 is "last season" — but on 2026-08-25 that month held 1,845
    // passengers of open bookings against September 2025's 21,139. The plan
    // would have proposed a near-dead autumn with every figure agreeing.
    if (keyOf(c) >= nowKey) return 'still taking bookings — not a full month of trading yet'
    if (c.pax <= 0) return 'no sales recorded'
    return null
  }

  // ★ The planner's explicit choice for THIS month wins outright.
  const picked = monthSources[monthKey]
  if (picked) {
    const c = candidates.find((x) => x.season === picked)
    if (c) {
      const why = reject(c)
      considered.push({ season: c.season, yr: c.yr, pax: c.pax, rejected: why })
      if (!why) {
        return {
          chosen: c, own: c.season === candidates[0]?.season, considered,
          basis: `${c.season} sales for ${mName} — your choice`,
        }
      }
      // Chosen but unusable. Say so and fall through rather than silently
      // substituting something the planner did not ask for (law 8).
      considered.length = 0
      considered.push({ season: c.season, yr: c.yr, pax: c.pax, rejected: `${why} — your choice could not be used` })
    } else {
      considered.push({ season: picked, yr: 0, pax: 0, rejected: 'that season has no data loaded' })
    }
  }

  let chosen: SeasonCandidate | null = null
  let own = false
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i]
    const why = reject(c)
    if (!considered.some((x) => x.season === c.season && x.yr === c.yr)) {
      considered.push({ season: c.season, yr: c.yr, pax: c.pax, rejected: why })
    }
    if (why || chosen) continue
    if (i === 0) {
      // The month's OWN season. Use it only once it has actually traded — a
      // month still taking bookings is thinner than it will end up being.
      if (monthKey < nowKey) { chosen = c; own = true }
      continue
    }
    chosen = c
  }

  if (!chosen) {
    const skipped = considered.filter((x) => x.rejected).map((x) => `${x.season} (${x.rejected})`)
    return {
      chosen: null, own: false, considered,
      basis: skipped.length > 0
        ? `no usable sales for ${mName}: ${skipped.join('; ')}. This month cannot be modelled until there is evidence to model it on.`
        : `no sales data for ${mName} in any season on record.`,
    }
  }
  if (own) return { chosen, own, considered, basis: `this season's own sales` }

  const passed = considered.filter((x) => x.rejected && x.season !== chosen!.season)
  const why = passed.length > 0
    ? ` — ${passed.map((x) => `${x.season} ${x.rejected}`).join(', ')}`
    : (monthKey >= nowKey ? ' — this season is still taking bookings' : '')
  return {
    chosen, own, considered,
    basis: `${chosen.season} sales for ${mName}${why}`,
  }
}

/**
 * The two-season form, kept because the season table and two gates already read
 * it. It DELEGATES to `chooseSalesBasis` so there is one rule, not two.
 */
export function chooseSalesSeason(args: {
  monthKey: string
  nowKey: string
  thisPax: number
  prevPax: number
  hasPrev: boolean
  disruptedMonths: string[]
}): { usePrev: boolean; isDisrupted: boolean } {
  const { monthKey, nowKey, thisPax, prevPax, hasPrev, disruptedMonths } = args
  const yr = Number(monthKey.slice(0, 4))
  const b = chooseSalesBasis({
    monthKey, nowKey, disruptedMonths,
    candidates: hasPrev
      ? [{ season: 'this', yr, pax: thisPax }, { season: 'prev', yr: yr - 1, pax: prevPax }]
      : [{ season: 'this', yr, pax: thisPax }],
  })
  const isDisrupted = disruptedMonths.includes(monthKey) && hasPrev
  // The historic rule also preferred last season for a FUTURE month that was
  // currently thinner than last year's — a forecast, not a correction. Keep it.
  const thinner = monthKey >= nowKey && prevPax > thisPax && hasPrev
  return { usePrev: isDisrupted || thinner || (b.chosen?.season === 'prev'), isDisrupted }
}

// ---------------------------------------------------------------------------
//  4. Reading a banked month BACK — what the screen shows
// ---------------------------------------------------------------------------

/** A banked colour plan as stored in a slice's `state_json.dayPlans`. */
export interface SeasonDayPlanLike {
  colour: string
  dayClass: string
  dates: string[]
  spans?: DayClassSpan[]
  deps?: Departure[]
  dl?: number
}

/** What a month's banked build actually contains, for the screen. */
export interface BankedView {
  /** The timetable to show. Empty means the build produced nothing that pays. */
  deps: Departure[]
  /** How to name it on screen — null when there is nothing to name. */
  label: string | null
  /** Timetables that built no train that pays. A real finding, never hidden. */
  empty: string[]
}

/**
 * How to name a banked plan on screen. A plan built since 2026-08-25 covers the
 * whole week its colour runs, so naming a single day-type would be a lie; a plan
 * banked before then really was one day-type.
 */
export function planLabel(pl: SeasonDayPlanLike, classLabels: Record<string, string> = {}): string {
  const n = pl.dates?.length ?? 0
  return pl.spans?.length
    ? `the ${pl.colour} timetable — every one of its ${n} day${n === 1 ? '' : 's'}`
    : `${pl.colour} · ${classLabels[pl.dayClass] ?? pl.dayClass}`
}

/**
 * Read a banked month back the way the SCREEN reads it.
 *
 * This is the gate-able core of the two defects that escaped in PRs #224/#225:
 * a build that was real but invisible, and a month whose headline timetable was
 * banked EMPTY so the screen fell back to the real working timetable and the
 * month read as never modelled with a zero delta.
 *
 * The rules, in precedence order:
 *   1. The banked headline timetable, if it holds anything.
 *   2. Otherwise the largest plan that ACTUALLY BUILT something — never the
 *      real WTT dressed up as a design.
 *   3. Otherwise nothing, and `empty` names what failed to pay (law 8: an empty
 *      result is an answer, so say it on screen).
 */
export function readBankedView(
  stateJson: string,
  classLabels: Record<string, string> = {}
): BankedView | null {
  try {
    const st = JSON.parse(stateJson) as { deps?: Departure[]; dayPlans?: SeasonDayPlanLike[] }
    const plans = st.dayPlans ?? []
    // A whole-week label already carries its day count; a legacy day-type
    // label does not, so only that one gets the count appended.
    const empty = plans
      .filter((pl) => (pl.deps?.length ?? 0) === 0)
      .map((pl) => {
        const n = pl.dates?.length ?? 0
        const lbl = planLabel(pl, classLabels)
        return pl.spans?.length ? lbl : `${lbl} (${n} day${n === 1 ? '' : 's'})`
      })
    let deps = Array.isArray(st.deps) ? st.deps : []
    let label: string | null = null
    if (deps.length === 0) {
      const best = [...plans]
        .filter((pl) => (pl.deps?.length ?? 0) > 0)
        .sort((a, b) => (b.dates?.length ?? 0) - (a.dates?.length ?? 0))[0]
      if (best) { deps = best.deps ?? []; label = planLabel(best, classLabels) }
    } else {
      const match = [...plans]
        .filter((pl) => (pl.deps?.length ?? 0) === deps.length)
        .sort((a, b) => (b.dates?.length ?? 0) - (a.dates?.length ?? 0))[0]
      if (match) label = planLabel(match, classLabels)
    }
    if (deps.length === 0) return { deps: [], label: null, empty }
    return { deps: deps.map((d) => ({ ...d })).sort((a, b) => a.minutes - b.minutes), label, empty }
  } catch { return null }
}

/** Where the design grid's departures come from, and why. ONE owner. */
export type SeedSource = 'banked' | 'modelled-empty' | 'wtt' | 'sales'

/**
 * The seeding precedence, as a pure decision so it can be gated.
 *
 * A BANKED build always beats the real working timetable — PR #224's bug was
 * the WTT re-seeding over a finished design, which made "Build the modelling"
 * look like it did nothing and made the month compare today with itself.
 *
 * And a month that WAS modelled to an empty answer shows the EMPTY ANSWER, not
 * the real timetable. Chris, 2026-08-25, on a March that had been correctly
 * modelled as "no train pays": *"march is still garbage"* — the panel had
 * fallen back to thebigplan's timetable and filled the screen with thirteen
 * workings carrying nobody. An empty result is an answer; dressing it in the
 * railway's real timetable makes it look like a broken design instead (law 8).
 * What the railway runs today belongs on the Current tab, which is exactly
 * where it is.
 */
export function chooseSeedSource(args: {
  hasBanked: boolean
  /** A slice exists for this month and its build produced no working at all. */
  modelledEmpty?: boolean
  hasWtt: boolean
}): SeedSource {
  if (args.hasBanked) return 'banked'
  if (args.modelledEmpty) return 'modelled-empty'
  if (args.hasWtt) return 'wtt'
  return 'sales'
}

// ---------------------------------------------------------------------------
//  5. WHICH DATE to ask thebigplan for a month's timetable
// ---------------------------------------------------------------------------

/**
 * The probe date used to fetch a month's working timetable.
 *
 * thebigplan serves the timetable whose effective window COVERS the date you
 * ask about. The model used to ask about the 15th of the month, which is not a
 * date the railway necessarily runs — and for a month that straddles a
 * timetable change it is the WRONG timetable.
 *
 * March 2026 is the case that proves it. Autumn/Winter 2025 runs to 20 March
 * and publishes only PINK and RED. Spring/Summer 2026 starts on 21 March and
 * publishes BLUE, PINK, RED and YELLOW. Every one of March's nine running days
 * is 21 March or later, and seven of them are YELLOW — but asking about
 * 15 March returned the WINTER timetable, which has no Yellow pattern at all.
 * So those seven days had no baseline: no operating window, no observed path
 * budget, and nothing on the current side to compare against. March was never
 * modelled. Three handovers recorded it as "honestly empty under the profit
 * floor"; that diagnosis was wrong.
 *
 * So: ask about a date the railway ACTUALLY RUNS. The month's first running day
 * is the right probe because the timetable in force on it is, by definition, the
 * one the month's service begins under.
 */
export function baselineProbeDate(args: {
  monthKey: string
  calendarDays: CalendarDay[]
  appliedDays: AppliedDay[] | null
}): string {
  // NOTE FOR THE NEXT READER: every baseline fetch in the app must come through
  // here. On 2026-08-25 this was fixed in the season cache but MISSED in the
  // active-month fetch and the pattern-switch refetch, so the season built
  // against Spring/Summer 2026 while the screen showed the planner Autumn/
  // Winter 2025's RED timetable for a March that runs YELLOW. Fixing one of
  // three callers of the same question is the defect this codebase keeps
  // producing. `baseline.test.ts` now greps the source to keep it at one.
  const { monthKey: mk, calendarDays, appliedDays } = args
  const fromSet = (appliedDays ?? [])
    .filter((d) => d.date.startsWith(mk) && d.running)
    .map((d) => d.date).sort()
  if (fromSet.length > 0) return fromSet[0]
  const fromCal = calendarDays
    .filter((d) => d.date.startsWith(mk) && d.run)
    .map((d) => d.date).sort()
  if (fromCal.length > 0) return fromCal[0]
  // Nothing runs (or the calendar has not loaded): the 15th is as good a guess
  // as any, and nothing will be designed for the month anyway.
  return `${mk}-15`
}

/**
 * Does this month's service straddle a timetable change?
 *
 * If some running dates fall outside the effective window of the timetable we
 * fetched, those dates are being designed against a timetable that does not
 * cover them. That must be SAID, not hidden (law 8) — an empty or wrong answer
 * reported as a result is the whole problem.
 */
export function straddleWarning(args: {
  monthKey: string
  effectiveFrom?: string
  effectiveTo?: string
  runningDates: string[]
}): string | null {
  const { effectiveFrom, effectiveTo, runningDates } = args
  if (!effectiveFrom || !effectiveTo) return null
  const outside = runningDates.filter((d) => d < effectiveFrom || d > effectiveTo).sort()
  if (outside.length === 0) return null
  return `${outside.length} running day${outside.length === 1 ? '' : 's'} (${outside[0]}${outside.length > 1 ? ` to ${outside[outside.length - 1]}` : ''}) fall outside the fetched timetable's window ${effectiveFrom} to ${effectiveTo} — this month straddles a timetable change`
}

/**
 * Every colour this month runs that the fetched timetable has NO pattern for.
 *
 * This is the check that would have caught March: seven Yellow days against a
 * timetable with no Yellow. A colour with no pattern cannot be given an
 * operating window or a path budget, so it must be named on screen rather than
 * quietly contributing nothing.
 */
export function coloursWithoutPattern(args: {
  groups: SeasonDayGroup[]
  patternCodes: string[]
}): { colour: string; days: number }[] {
  const have = new Set(args.patternCodes.map((c) => c.toUpperCase()))
  return args.groups
    .filter((g) => !unscorableColour(g.colour) && !have.has(`TT-${g.colour.toUpperCase()}`))
    .map((g) => ({ colour: g.colour, days: g.dates.length }))
}

// ---------------------------------------------------------------------------
//  6. WHICH MONTHS a season is modelled over
// ---------------------------------------------------------------------------

/**
 * The months of a season, from the SEASON ITSELF.
 *
 * Chris, 2026-08-25: *"the entire point of this studio is that you're actually
 * designing the 2027 running season."* A season being designed has sold nothing,
 * so deriving its months from ticket sales gave it NO MONTHS — the Studio came
 * up empty and there was nothing to design. The months belong to the season's
 * date range; whether any given month runs trains is the running calendar's
 * business, and a month with no running days already says so.
 *
 * Falls back to `salesMonths` when the range is unavailable (an older API
 * build), which is exactly the previous behaviour.
 */
export function seasonMonths(args: {
  from: string | null | undefined
  to: string | null | undefined
  salesMonths: string[]
}): string[] {
  const { from, to, salesMonths } = args
  if (!from || !to || from > to) return [...salesMonths].sort()
  const out: string[] = []
  let y = Number(from.slice(0, 4))
  let m = Number(from.slice(5, 7))
  const endY = Number(to.slice(0, 4))
  const endM = Number(to.slice(5, 7))
  // 24 is a hard stop: a malformed range must not spin.
  for (let guard = 0; guard < 24 && (y < endY || (y === endY && m <= endM)); guard++) {
    out.push(`${y}-${String(m).padStart(2, '0')}`)
    m += 1
    if (m > 12) { m = 1; y += 1 }
  }
  return out
}

/**
 * The dates of a running calendar that belong to THIS season.
 *
 * A running calendar is a list of dated days, so it belongs to the season its
 * dates fall in. Switching the Studio from 2026/27 to 2027/28 left 2026's
 * applied set in play: not one of its dates fell in 2027/28, so every 2027
 * date read as "not running" and the season had nothing to build (2026-10-27).
 * Both owners of the applied calendar — the saved-set loader and the
 * thebigplan default — keep only a calendar this returns dates for. With no
 * season bounds (an older API build) every date is kept, as before.
 */
export function datesInSeason<T extends { date: string }>(
  days: T[] | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined,
): T[] {
  if (!days) return []
  if (!from || !to) return days
  return days.filter((d) => d.date >= from.slice(0, 10) && d.date <= to.slice(0, 10))
}

/**
 * What the automatic rule WOULD pick for a month — so the per-month dropdown can
 * show it as its default option ("Automatic — 2025/2026") and the planner can
 * see the reasoning before overriding it.
 */
export function automaticSource(args: {
  monthKey: string
  nowKey: string
  candidates: SeasonCandidate[]
  monthSources?: Record<string, string>
  disruptedMonths?: string[]
}): { season: string | null; basis: string } {
  const b = chooseSalesBasis({
    monthKey: args.monthKey,
    nowKey: args.nowKey,
    candidates: args.candidates,
    // Deliberately WITHOUT this month's own override, so the answer is what the
    // rule would do unaided — but WITH the others, because their redirections
    // still make them unrepresentative as sources.
    monthSources: Object.fromEntries(Object.entries(args.monthSources ?? {}).filter(([k]) => k !== args.monthKey)),
    disruptedMonths: args.disruptedMonths ?? [],
  })
  return { season: b.chosen?.season ?? null, basis: b.basis }
}
